/**
 * The platform did not answer the first question a page asks (who is signed
 * in), because the API is restarting or the network dropped. Rather than the
 * sign-in page, which would read as "you were signed out", this asks again by
 * itself, sooner at first, and carries on the moment it gets an answer.
 *
 * Invisible for the first second: a restart that short is never seen.
 */
import { useEffect, useRef, useState } from "react";
import { Loader2Icon, RefreshCwIcon, WifiOffIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useErrorCopy } from "./copy";
import { PageFrame } from "./error-fallback";

/** 1 s, 2 s, 4 s, 8 s, then every 15 s. */
const delayFor = (attempt: number) => Math.min(1000 * 2 ** attempt, 15_000);

export function ReconnectingScreen({ retry, retrying }: { retry: () => unknown; retrying: boolean }) {
  const copy = useErrorCopy();
  const [attempt, setAttempt] = useState(0);
  const retryRef = useRef(retry);
  useEffect(() => {
    retryRef.current = retry;
  });

  useEffect(() => {
    const id = window.setTimeout(() => {
      void retryRef.current();
      setAttempt((n) => n + 1);
    }, delayFor(attempt));
    return () => window.clearTimeout(id);
  }, [attempt]);

  // Back online, or back to this tab: ask now rather than at the next tick.
  useEffect(() => {
    const now = () => {
      void retryRef.current();
      setAttempt(0);
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") now();
    };
    window.addEventListener("online", now);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", now);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  return (
    <div className="animate-in fade-in-0 fill-mode-both delay-1000 duration-500">
      <PageFrame>
        <Card className="shadow-lg shadow-black/5">
          <CardContent className="py-4">
            <section role="status" aria-live="polite" className="flex flex-col items-center gap-6 text-center">
              <div
                aria-hidden
                className="bg-muted text-muted-foreground ring-border flex size-12 items-center justify-center rounded-2xl ring-1 ring-inset"
              >
                <WifiOffIcon className="size-5" />
              </div>
              <div className="flex flex-col gap-2">
                <h1 className="text-xl font-semibold tracking-tight text-balance">{copy.reconnectTitle}</h1>
                <p className="text-muted-foreground text-sm/relaxed text-balance">{copy.reconnectBody}</p>
              </div>
              <Button
                size="lg"
                variant="outline"
                disabled={retrying}
                onClick={() => {
                  void retry();
                  setAttempt(0);
                }}
              >
                {retrying ? (
                  <Loader2Icon data-icon="inline-start" className="animate-spin" />
                ) : (
                  <RefreshCwIcon data-icon="inline-start" />
                )}
                {copy.tryNow}
              </Button>
            </section>
          </CardContent>
        </Card>
      </PageFrame>
    </div>
  );
}
